/**
 * The flows gallery's search: an instant match on the brand, over the cards
 * the page already holds — no index round-trip. A visitor looking for a
 * flow knows the brand ("notion", "atlas…"); the semantic index answers a
 * different question and stays with the templates gallery.
 *
 * Pure. Ranked so the brands whose name starts with the query come first,
 * then a domain that starts with it, then a name or domain that contains
 * it, then a title that contains it; ties keep the gallery's order.
 */
export type BrandSearchableCard = {
  brandName: string
  brandDomain: string
  title: string
}

/** Lowercase, no diacritics, one space between words. */
export function normalizeBrandQuery(query: string): string {
  return query
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

/** `notion.so` → `notion`; `mail.trigger.dev` → `mail trigger`. */
function domainWords(domain: string): string {
  const labels = normalizeBrandQuery(domain).split('.')
  return labels.slice(0, -1).join(' ') || (labels[0] ?? '')
}

function startsAtAWord(haystack: string, needle: string): boolean {
  if (haystack.startsWith(needle)) {
    return true
  }
  return haystack.split(/[\s.-]+/).some((word) => word.startsWith(needle))
}

function rankOf(card: BrandSearchableCard, query: string): number | null {
  const name = normalizeBrandQuery(card.brandName)
  const domain = domainWords(card.brandDomain)
  if (startsAtAWord(name, query)) {
    return 0
  }
  if (startsAtAWord(domain, query)) {
    return 1
  }
  if (name.includes(query) || domain.includes(query)) {
    return 2
  }
  if (normalizeBrandQuery(card.title).includes(query)) {
    return 3
  }
  return null
}

export function matchFlowCardsByBrand<Card extends BrandSearchableCard>(
  cards: ReadonlyArray<Card>,
  query: string
): Array<Card> {
  const needle = normalizeBrandQuery(query)
  if (!needle) {
    return [...cards]
  }
  return cards
    .map((card, index) => ({ card, index, rank: rankOf(card, needle) }))
    .filter(
      (entry): entry is { card: Card; index: number; rank: number } =>
        entry.rank !== null
    )
    .sort((left, right) => left.rank - right.rank || left.index - right.index)
    .map((entry) => entry.card)
}
